// FORGED Ed Systems — DEPLOY010 — v0.10.0 — ReportViewPage.tsx
// Last touched: 2026-06-14
//
// /r/:reportId — view a generated strategic plan, OR show progress while it
// generates in the background. Polls the reports table every 3 seconds until
// status flips from 'queued'/'running' to 'complete' or 'failed'.
//
// DEPLOY006 — added PDF export. Per CLAUDE.md §5.4 the PDF generator
// (`generateForgedsightReport()`) lives INSIDE this page component, not in
// a separate file. Per CLAUDE.md §13.5 every printed page carries the
// "Confidential — FORGED Educational Systems" footer and FORGED branding header.
// Export button is gated on isGenerating to prevent stale-state capture.
//
// DEPLOY008 — added sanitizePdfText() helper for jsPDF helvetica encoding.
//
// DEPLOY009 — report PDF visual redesign: accent header, hero stat + CI bar,
//             confidence/severity chips, phase timeline, callout boxes.
//
// DEPLOY010 — on-screen complete-report view restyled to MATCH the PDF
//             (scoped `fsr-` styles). Data/logic and the PDF builder unchanged.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { jsPDF } from "jspdf";

type ReportRow = {
  report_id: string;
  account_id: string;
  evaluation_curriculum: string;
  operating_mode: string;
  agent_outputs: Record<string, unknown>;
  status: "queued" | "running" | "complete" | "failed";
  error_message: string | null;
  hard_confidence_gate_passed: boolean;
  calibrated_abstention_notes: string | null;
  generated_at: string;
  generation_started_at: string | null;
  generation_completed_at: string | null;
};

const POLL_INTERVAL_MS = 3000;

const STATUS_MESSAGES = [
  "Loading curriculum framework…",
  "Running the six-agent analysis…",
  "A-3 Authority is binding claims to clauses…",
  "A-1 Instructor is sequencing your pacing plan…",
  "A-2 Challenger is auditing booth-hours…",
  "A-4 Classroom Ops is allocating instructor attention…",
  "A-5 Readiness is projecting your cohort pass rate…",
];

const CURRICULUM_LABELS: Record<string, string> = {
  aws_sense: "AWS SENSE",
  nccer_welding: "NCCER Welding",
  american_welding_program: "AWP (American Welding Program)",
  tx_teks_teacher_generated: "TX TEKS — Teacher-Generated",
  forged_code_first: "FORGED Code-First (pilot)",
};

const OPERATING_MODE_LABELS: Record<string, string> = {
  start_of_year: "Start of year",
  mid_year: "Mid-year recovery",
  cert_prep_window: "Cert prep window",
};

// On-screen chip tone helpers (mirror the PDF's chip semantics):
// severity high=red/med=amber (worse), confidence high=green/med=amber (surer).
function sevTone(sev: any): string {
  const s = String(sev || "").toLowerCase();
  if (s.indexOf("high") >= 0) return "red";
  if (s.indexOf("med") >= 0) return "amber";
  return "gray";
}
function confTone(conf: any): string {
  const c = String(conf || "").toLowerCase();
  if (c.indexOf("high") >= 0) return "green";
  if (c.indexOf("med") >= 0) return "amber";
  return "gray";
}

const FSR_STYLES = `
.fsr{max-width:820px;margin:0 auto 40px;background:#fff;color:#2f3744;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,.08),0 12px 40px rgba(0,0,0,.06);overflow:hidden;line-height:1.6;font-size:14px}
.fsr *{box-sizing:border-box}
.fsr-top{display:flex;justify-content:space-between;align-items:center;padding:14px 36px;border-top:4px solid #c2410c;background:#fbfbfc;border-bottom:1px solid #e6e8ec}
.fsr-brand{font-weight:800;letter-spacing:-.3px;color:#1a1f2b;font-size:15px}
.fsr-brand span{color:#c2410c}
.fsr-conf{font-size:11px;text-transform:uppercase;letter-spacing:.12em;color:#6b7280}
.fsr-actions{display:flex;gap:10px;padding:18px 36px 0;flex-wrap:wrap}
.fsr-btn{appearance:none;border:0;border-radius:8px;padding:10px 16px;font-size:13px;font-weight:700;cursor:pointer;text-decoration:none;display:inline-block}
.fsr-btn-primary{background:#c2410c;color:#fff}
.fsr-btn-primary:disabled{opacity:.6;cursor:default}
.fsr-btn-secondary{background:#f3f4f6;color:#374151}
.fsr-body{padding:26px 36px 40px}
.fsr-eyebrow{font-size:11px;text-transform:uppercase;letter-spacing:.16em;color:#c2410c;font-weight:700;margin-bottom:6px}
.fsr-h1{font-size:28px;line-height:1.15;margin:0 0 16px;color:#1a1f2b;letter-spacing:-.5px;font-weight:800}
.fsr-meta{display:grid;grid-template-columns:1fr 1fr;border:1px solid #e6e8ec;border-radius:8px;overflow:hidden;margin-bottom:14px}
.fsr-meta>div{padding:11px 16px;border-bottom:1px solid #e6e8ec}
.fsr-meta>div:nth-child(odd){border-right:1px solid #e6e8ec}
.fsr-meta .k{font-size:10.5px;text-transform:uppercase;letter-spacing:.1em;color:#6b7280}
.fsr-meta .v{font-weight:600;color:#1a1f2b;margin-top:1px;word-break:break-word}
.fsr-gate{display:inline-block;background:#f0fdf4;border:1px solid #bbf7d0;color:#15803d;padding:6px 12px;border-radius:999px;font-size:12px;font-weight:700}
.fsr-gate.abst{background:#fffbeb;border-color:#fde68a;color:#b45309}
.fsr-hero{margin:24px 0 8px;border:1px solid #e6e8ec;border-radius:10px;padding:22px 24px;background:linear-gradient(180deg,#fff,#fcfcfd)}
.fsr-hero .lbl{font-size:11px;text-transform:uppercase;letter-spacing:.14em;color:#6b7280;font-weight:700}
.fsr-stat{font-size:50px;font-weight:800;color:#1a1f2b;line-height:1;margin:6px 0 2px;letter-spacing:-1.5px}
.fsr-stat small{font-size:17px;font-weight:700;color:#6b7280;margin-left:8px;letter-spacing:0}
.fsr-ci-cap{font-size:12px;color:#6b7280;margin:14px 0 7px}
.fsr-ci-track{position:relative;height:10px;background:#eef0f3;border-radius:999px}
.fsr-ci-band{position:absolute;top:0;height:10px;background:#fed7aa;border-radius:999px}
.fsr-ci-marker{position:absolute;top:-4px;width:3px;height:18px;background:#c2410c;border-radius:2px}
.fsr-ci-scale{display:flex;justify-content:space-between;font-size:10.5px;color:#6b7280;margin-top:6px}
.fsr-sec{margin-top:26px}
.fsr-sec-h{display:flex;align-items:center;gap:12px;margin:0 0 12px}
.fsr-sec-h .bar{width:4px;height:20px;background:#c2410c;border-radius:2px}
.fsr-sec-h h2{font-size:17px;margin:0;color:#1a1f2b;letter-spacing:-.2px}
.fsr-sec h3{font-size:13px;margin:16px 0 8px;color:#1a1f2b}
.fsr-sec p{margin:0 0 12px}
.fsr-muted{color:#6b7280}
.fsr-item{border-left:3px solid #e6e8ec;border-top:1px solid #f1f2f4;padding:12px 0 12px 14px}
.fsr-item.red{border-left-color:#b91c1c}.fsr-item.amber{border-left-color:#b45309}.fsr-item.green{border-left-color:#15803d}.fsr-item.gray{border-left-color:#9ca3af}
.fsr-item-head{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
.fsr-lead{font-weight:700;color:#1a1f2b}
.fsr-chip{flex:none;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:3px 9px;border-radius:999px;white-space:nowrap;height:fit-content}
.fsr-red{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca}
.fsr-amber{background:#fffbeb;color:#b45309;border:1px solid #fde68a}
.fsr-green{background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0}
.fsr-gray{background:#f3f4f6;color:#4b5563;border:1px solid #d1d5db}
.fsr-cite{font-size:11.5px;color:#6b7280;font-style:italic;margin-top:6px}
.fsr-mit{margin-top:9px;font-size:13px;background:#f8fafc;border:1px solid #eef1f4;border-radius:6px;padding:9px 12px}
.fsr-tier{display:inline-block;font-size:11px;font-weight:700;color:#4b5563;background:#f3f4f6;border:1px solid #d1d5db;border-radius:999px;padding:3px 10px;margin-bottom:10px}
.fsr-callout{border:1px solid #e6e8ec;background:#fafafa;border-radius:10px;padding:16px 18px;margin:8px 0 12px}
.fsr-callout.accent{border-color:#fed7aa;background:#fff7ed}
.fsr-callout .h{font-weight:800;color:#1a1f2b;margin-bottom:6px}
.fsr-callout .price{font-weight:800;color:#c2410c;margin-top:8px}
.fsr-callout .note{font-size:12px;color:#6b7280;margin-top:2px}
.fsr-timeline{margin-top:6px}
.fsr-phase{display:flex;gap:14px;position:relative;padding-bottom:18px}
.fsr-phase:not(:last-child)::before{content:"";position:absolute;left:13px;top:30px;bottom:0;width:2px;background:#fed7aa}
.fsr-phase-num{flex:none;width:28px;height:28px;border-radius:50%;background:#c2410c;color:#fff;font-weight:800;font-size:13px;display:flex;align-items:center;justify-content:center;z-index:1}
.fsr-phase-num.last{background:#15803d}
.fsr-phase-title{font-weight:700;color:#1a1f2b}
.fsr-week{display:inline-block;font-size:11px;font-weight:700;color:#c2410c;background:#fff7ed;border:1px solid #fed7aa;border-radius:999px;padding:1px 9px;margin-left:8px}
.fsr-phase-focus{margin-top:3px;font-size:13px;color:#2f3744}
.fsr-footmeta{margin-top:28px;padding-top:14px;border-top:1px solid #e6e8ec;font-size:11px;color:#6b7280;word-break:break-word}
`;

export default function ReportViewPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ reportId: string }>();
  const reportId = params.reportId;

  const [report, setReport] = useState<ReportRow | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusIndex, setStatusIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!loading && !session) {
      navigate("/sign-in");
    }
  }, [loading, session, navigate]);

  useEffect(() => {
    if (!report || report.status === "complete" || report.status === "failed") {
      return;
    }
    const intervalId = window.setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [report]);

  useEffect(() => {
    if (!session || !reportId) return;
    var cancelled = false;
    var timeoutId: number | null = null;

    async function pollOnce() {
      const res = await supabase
        .from("reports")
        .select(
          "report_id, account_id, evaluation_curriculum, operating_mode, agent_outputs, status, error_message, hard_confidence_gate_passed, calibrated_abstention_notes, generated_at, generation_started_at, generation_completed_at"
        )
        .eq("report_id", reportId)
        .single();

      if (cancelled) return;

      if (res.error) {
        setErrorMsg(res.error.message);
        return;
      }
      if (!res.data) {
        setErrorMsg("Report not found.");
        return;
      }

      const row = res.data as ReportRow;
      setReport(row);

      if (row.status === "queued" || row.status === "running") {
        timeoutId = window.setTimeout(pollOnce, POLL_INTERVAL_MS);
      }
    }

    pollOnce();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [session, reportId]);

  function generateForgedsightReport() {
    if (isGenerating) return;
    const r = report;
    if (!r || r.status !== "complete") return;

    setIsGenerating(true);
    try {
      buildAndSavePdf(r);
    } catch (err) {
      var message = "unknown error";
      if (err instanceof Error) {
        message = err.message;
      }
      alert(
        "PDF export failed: " +
          message +
          ". Try again or contact support@forgedsight.com with report ID " +
          r.report_id +
          "."
      );
    } finally {
      setIsGenerating(false);
    }
  }

  if (loading) {
    return (
      <main className="container">
        <p>Loading…</p>
      </main>
    );
  }

  if (!session) {
    return null;
  }

  if (errorMsg) {
    return (
      <main className="container">
        <h1>Couldn't load that plan</h1>
        <p className="error-banner">{errorMsg}</p>
        <Link to="/dashboard">← Back to dashboard</Link>
      </main>
    );
  }

  if (!report) {
    return (
      <main className="container">
        <p>Loading your plan…</p>
      </main>
    );
  }

  if (report.status === "queued" || report.status === "running") {
    return (
      <main className="container">
        <header className="brand-header">
          <h1>Generating your strategic plan</h1>
          <p className="tagline">
            This usually takes 60-120 seconds. We'll refresh automatically.
          </p>
        </header>

        <section className="hero">
          <div className="info-card">
            <p style={{ fontSize: "1.1rem", margin: 0 }}>
              {STATUS_MESSAGES[statusIndex]}
            </p>
          </div>
          <p style={{ marginTop: "1.5rem", color: "var(--color-text-muted)" }}>
            You can close this tab and come back later — your plan will be in your
            dashboard when it's ready.
          </p>
          <Link to="/dashboard" className="cta secondary" style={{ marginTop: "1rem" }}>
            ← Back to dashboard
          </Link>
        </section>
      </main>
    );
  }

  if (report.status === "failed") {
    return (
      <main className="container">
        <h1>Plan generation failed</h1>
        <p className="error-banner">
          {report.error_message || "An unknown error occurred during generation."}
        </p>
        <p style={{ color: "var(--color-text-muted)", marginTop: "1rem" }}>
          You can try submitting the intake form again. If this keeps happening,
          contact support@forgedsight.com with this report ID:{" "}
          <code>{report.report_id}</code>.
        </p>
        <Link to="/dashboard" className="cta secondary" style={{ marginTop: "1rem" }}>
          ← Back to dashboard
        </Link>
      </main>
    );
  }

  // ---------- Status: complete (redesigned to match the PDF) ----------
  const outputs = report.agent_outputs as {
    a1?: any;
    a2?: any;
    a3?: any;
    a4?: any;
    a5?: any;
  };

  const curriculumLabel =
    CURRICULUM_LABELS[report.evaluation_curriculum] || report.evaluation_curriculum;
  const modeLabel = OPERATING_MODE_LABELS[report.operating_mode] || report.operating_mode;

  const a5 = outputs.a5;
  const pct = a5 ? Math.round(a5.projected_pass_rate_pct || 0) : 0;
  const ci = a5 && a5.wilson_ci_95 && a5.wilson_ci_95.length === 2 ? a5.wilson_ci_95 : null;
  const lo = ci ? Math.max(0, Math.min(100, Math.round(ci[0]))) : 0;
  const hi = ci ? Math.max(0, Math.min(100, Math.round(ci[1]))) : 0;

  return (
    <main className="container">
      <style>{FSR_STYLES}</style>
      <div className="fsr">
        <div className="fsr-top">
          <div className="fsr-brand">
            forged<span>sight</span>
          </div>
          <div className="fsr-conf">Confidential · FORGED Educational Systems</div>
        </div>

        <div className="fsr-actions">
          <button
            onClick={generateForgedsightReport}
            className="fsr-btn fsr-btn-primary"
            disabled={isGenerating}
          >
            {isGenerating ? "Generating PDF…" : "Export Program Report (PDF)"}
          </button>
          <Link to="/dashboard" className="fsr-btn fsr-btn-secondary">
            ← Back to dashboard
          </Link>
        </div>

        <div className="fsr-body">
          <div className="fsr-eyebrow">FORGED EDUCATIONAL SYSTEMS</div>
          <h1 className="fsr-h1">Strategic Plan</h1>

          <div className="fsr-meta">
            <div>
              <div className="k">Curriculum</div>
              <div className="v">{curriculumLabel}</div>
            </div>
            <div>
              <div className="k">Operating mode</div>
              <div className="v">{modeLabel}</div>
            </div>
            <div>
              <div className="k">Generated</div>
              <div className="v">{new Date(report.generated_at).toLocaleString()}</div>
            </div>
            <div>
              <div className="k">Report ID</div>
              <div className="v">{report.report_id}</div>
            </div>
          </div>
          <span className={"fsr-gate" + (report.hard_confidence_gate_passed ? "" : " abst")}>
            {report.hard_confidence_gate_passed
              ? "Hard confidence gate: passed"
              : "Hard confidence gate: abstained"}
          </span>

          {a5 && (
            <div className="fsr-hero">
              <div className="lbl">Projected cohort outcome</div>
              <div className="fsr-stat">
                {pct}%<small>projected pass rate</small>
              </div>
              {ci ? (
                <>
                  <div className="fsr-ci-cap">
                    95% confidence interval: {lo}% to {hi}%
                  </div>
                  <div className="fsr-ci-track">
                    <div
                      className="fsr-ci-band"
                      style={{ left: lo + "%", width: hi - lo + "%" }}
                    />
                    <div className="fsr-ci-marker" style={{ left: pct + "%" }} />
                  </div>
                  <div className="fsr-ci-scale">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </>
              ) : (
                <div className="fsr-ci-cap">95% confidence interval: not available</div>
              )}
              {a5.ci_widened_for && a5.ci_widened_for.length > 0 && (
                <p className="fsr-muted" style={{ margin: "10px 0 0" }}>
                  CI widened for: {a5.ci_widened_for.join(", ")}
                </p>
              )}
            </div>
          )}

          {outputs.a3 && (
            <section className="fsr-sec">
              <div className="fsr-sec-h">
                <div className="bar" />
                <h2>Curriculum alignment</h2>
              </div>
              <p>{outputs.a3.curriculum_alignment}</p>

              <h3>Accommodation pathway</h3>
              <p>{outputs.a3.accommodation_pathway}</p>

              {outputs.a3.instructional_gaps && outputs.a3.instructional_gaps.length > 0 && (
                <>
                  <h3>Instructional gaps</h3>
                  {outputs.a3.instructional_gaps.map((g: any, i: number) => (
                    <div key={i} className={"fsr-item " + confTone(g.confidence)}>
                      <div className="fsr-item-head">
                        <div>{g.gap}</div>
                        <span className={"fsr-chip fsr-" + confTone(g.confidence)}>
                          {(g.confidence || "?") + " confidence"}
                        </span>
                      </div>
                      {g.authority_cited && (
                        <div className="fsr-cite">Cites {g.authority_cited}</div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </section>
          )}

          {outputs.a1 && (
            <section className="fsr-sec">
              <div className="fsr-sec-h">
                <div className="bar" />
                <h2>Pacing plan</h2>
              </div>

              {outputs.a1.instructor_tier && (
                <div className="fsr-tier">Tier: {outputs.a1.instructor_tier}</div>
              )}

              {outputs.a1.strength_framing && (
                <div className="fsr-callout">
                  <p style={{ margin: 0 }}>{outputs.a1.strength_framing}</p>
                </div>
              )}

              {outputs.a1.pacing_plan && outputs.a1.pacing_plan.length > 0 && (
                <div className="fsr-timeline">
                  {outputs.a1.pacing_plan.map((p: any, i: number) => {
                    const ptitle = String(p.phase || "").replace(
                      /^\s*phase\s*\d+\s*[:\-–—]\s*/i,
                      ""
                    );
                    const last = i === outputs.a1.pacing_plan.length - 1;
                    return (
                      <div key={i} className="fsr-phase">
                        <div className={"fsr-phase-num" + (last ? " last" : "")}>
                          {i + 1}
                        </div>
                        <div>
                          <div className="fsr-phase-title">
                            {ptitle}
                            {p.weeks != null && (
                              <span className="fsr-week">{p.weeks} weeks</span>
                            )}
                          </div>
                          <div className="fsr-phase-focus">{p.focus}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {outputs.a1.instructor_guidance_band && (
                <>
                  <h3>Instructor guidance</h3>
                  <p>{outputs.a1.instructor_guidance_band}</p>
                </>
              )}
            </section>
          )}

          {outputs.a4 && (
            <section className="fsr-sec">
              <div className="fsr-sec-h">
                <div className="bar" />
                <h2>Classroom operations</h2>
              </div>

              {outputs.a4.booth_rotation && outputs.a4.booth_rotation.pattern && (
                <p>
                  <strong>Booth rotation:</strong> {outputs.a4.booth_rotation.pattern}
                </p>
              )}
              {outputs.a4.equipment_fit_analysis && (
                <p>{outputs.a4.equipment_fit_analysis}</p>
              )}

              {outputs.a4.operational_bottlenecks &&
                outputs.a4.operational_bottlenecks.length > 0 && (
                  <>
                    <h3>Operational bottlenecks</h3>
                    {outputs.a4.operational_bottlenecks.map((b: any, i: number) => (
                      <div key={i} className={"fsr-item " + sevTone(b.severity)}>
                        <div className="fsr-item-head">
                          <div className="fsr-lead">{b.bottleneck}</div>
                          <span className={"fsr-chip fsr-" + sevTone(b.severity)}>
                            {b.severity}
                          </span>
                        </div>
                        {b.mitigation && (
                          <div className="fsr-mit">
                            <strong>Mitigation:</strong> {b.mitigation}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
            </section>
          )}

          {outputs.a2 && outputs.a2.challenges && outputs.a2.challenges.length > 0 && (
            <section className="fsr-sec">
              <div className="fsr-sec-h">
                <div className="bar" />
                <h2>What we flagged</h2>
              </div>
              <p className="fsr-muted">
                A-2 Challenger reviewed the plan and flagged the following — verdict:{" "}
                <strong>{outputs.a2.verdict}</strong>.
              </p>
              {outputs.a2.challenges.map((c: any, i: number) => (
                <div key={i} className={"fsr-item " + sevTone(c.severity)}>
                  <div className="fsr-item-head">
                    <div className="fsr-lead">{c.flag}</div>
                    <span className={"fsr-chip fsr-" + sevTone(c.severity)}>
                      {c.severity}
                    </span>
                  </div>
                  <p style={{ margin: "6px 0 0" }}>{c.description}</p>
                  {c.numeric_citation && (
                    <div className="fsr-cite">{c.numeric_citation}</div>
                  )}
                </div>
              ))}
            </section>
          )}

          <section className="fsr-sec">
            <div className="fsr-callout accent">
              <div className="h">Want plate-level analysis?</div>
              Whatever curriculum your school uses, <strong>forgedweldscan</strong>{" "}
              tells you whether your students are on track to pass their certification
              test — before test day. It evaluates every weld against the same
              acceptance logic industrial inspectors use, and ranks failure modes by
              clause-level dominance.
              <div className="price">$1,500/year/school · up to 100 students</div>
              <div className="note">
                Pairs with any curriculum — AWS SENSE, NCCER, AWP, TX TEKS, or your own.
              </div>
            </div>
          </section>

          {report.calibrated_abstention_notes && (
            <section className="fsr-sec">
              <div className="fsr-sec-h">
                <div className="bar" style={{ background: "#9ca3af" }} />
                <h2>Where we abstained</h2>
              </div>
              <div className="fsr-callout">
                <p className="fsr-muted" style={{ margin: 0 }}>
                  {report.calibrated_abstention_notes}
                </p>
              </div>
            </section>
          )}

          <div className="fsr-footmeta">
            DEPLOY010 · v0.10.0 · Report ID: {report.report_id} · Hard confidence gate:{" "}
            {report.hard_confidence_gate_passed ? "passed" : "abstained"} · Generated:{" "}
            {new Date(report.generated_at).toLocaleString()}
          </div>
        </div>
      </div>
    </main>
  );
}

// ============================================================================
// PDF BUILDER (jsPDF) — kept in this file per CLAUDE.md §5.4
// ============================================================================
// Letter page (612pt × 792pt). 48pt margins. Helvetica throughout (built into
// jsPDF). Each page carries a FORGED branding header + Confidential footer
// (CLAUDE.md §13.5). DEPLOY009 redesign: drawing only — data flow unchanged.

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN_LEFT = 48;
const MARGIN_RIGHT = 48;
const MARGIN_TOP = 72;
const MARGIN_BOTTOM = 56;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
const CONTENT_BOTTOM = PAGE_HEIGHT - MARGIN_BOTTOM;

const INK: number[] = [26, 31, 43];
const BODY: number[] = [47, 55, 68];
const MUTED: number[] = [107, 114, 128];
const HAIR: number[] = [226, 229, 233];
const ACCENT: number[] = [194, 65, 12];
const ACCENT_TINT: number[] = [255, 247, 237];
const ACCENT_BORDER: number[] = [253, 215, 170];

type ChipStyle = { fg: number[]; bg: number[]; border: number[] };
const RED_CHIP: ChipStyle = { fg: [185, 28, 28], bg: [254, 242, 242], border: [254, 202, 202] };
const AMBER_CHIP: ChipStyle = { fg: [180, 83, 9], bg: [255, 251, 235], border: [253, 230, 138] };
const GREEN_CHIP: ChipStyle = { fg: [21, 128, 61], bg: [240, 253, 244], border: [187, 247, 208] };
const GRAY_CHIP: ChipStyle = { fg: [75, 85, 99], bg: [243, 244, 246], border: [209, 213, 219] };

type ItemOpts = {
  lead?: string;
  body?: string;
  chipLabel?: string;
  chip?: ChipStyle;
  cite?: string;
  sub?: string;
};

type CalloutOpts = {
  title?: string;
  body: string;
  emphasis?: string;
  note?: string;
  bg: number[];
  border: number[];
  titleColor?: number[];
};

function severityChip(sev: any): ChipStyle {
  const s = String(sev || "").toLowerCase();
  if (s.indexOf("high") >= 0) return RED_CHIP;
  if (s.indexOf("med") >= 0) return AMBER_CHIP;
  return GRAY_CHIP;
}

function confidenceChip(conf: any): ChipStyle {
  const c = String(conf || "").toLowerCase();
  if (c.indexOf("high") >= 0) return GREEN_CHIP;
  if (c.indexOf("med") >= 0) return AMBER_CHIP;
  return GRAY_CHIP;
}

function buildAndSavePdf(report: ReportRow) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const outputs = report.agent_outputs as {
    a1?: any;
    a2?: any;
    a3?: any;
    a4?: any;
    a5?: any;
  };

  const curriculumLabel =
    CURRICULUM_LABELS[report.evaluation_curriculum] || report.evaluation_curriculum;
  const modeLabel = OPERATING_MODE_LABELS[report.operating_mode] || report.operating_mode;

  let currentY = MARGIN_TOP;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.text("FORGED EDUCATIONAL SYSTEMS", MARGIN_LEFT, currentY);
  currentY += 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.text("Strategic Plan", MARGIN_LEFT, currentY);
  currentY += 22;

  const metaRows: string[][] = [
    ["Curriculum", curriculumLabel],
    ["Operating mode", modeLabel],
    ["Generated", new Date(report.generated_at).toLocaleString()],
    ["Report ID", report.report_id],
  ];
  const rowH = 20;
  const metaH = metaRows.length * rowH + 12;
  doc.setFillColor(250, 250, 251);
  doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
  doc.setLineWidth(0.75);
  doc.roundedRect(MARGIN_LEFT, currentY, CONTENT_WIDTH, metaH, 6, 6, "FD");
  let metaY = currentY + 18;
  for (let mr = 0; mr < metaRows.length; mr++) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text(sanitizePdfText(metaRows[mr][0].toUpperCase()), MARGIN_LEFT + 14, metaY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(INK[0], INK[1], INK[2]);
    doc.text(sanitizePdfText(metaRows[mr][1]), MARGIN_LEFT + 130, metaY);
    metaY += rowH;
  }
  currentY += metaH + 14;

  const gatePassed = report.hard_confidence_gate_passed;
  drawChip(
    doc,
    MARGIN_LEFT,
    currentY,
    gatePassed ? "Hard confidence gate: passed" : "Hard confidence gate: abstained",
    gatePassed ? GREEN_CHIP : AMBER_CHIP,
    false
  );
  currentY += 28;
  doc.setTextColor(0, 0, 0);

  if (outputs.a5) {
    const pct = Math.round(outputs.a5.projected_pass_rate_pct || 0);
    const ciArr =
      outputs.a5.wilson_ci_95 && outputs.a5.wilson_ci_95.length === 2
        ? (outputs.a5.wilson_ci_95 as number[])
        : null;
    currentY = drawOutcomeHero(doc, currentY, pct, ciArr);
    if (outputs.a5.ci_widened_for && outputs.a5.ci_widened_for.length > 0) {
      currentY = addParagraph(
        doc,
        "CI widened for: " + outputs.a5.ci_widened_for.join(", "),
        currentY,
        { muted: true }
      );
    }
  }

  if (outputs.a3) {
    currentY = addH2(doc, "Curriculum alignment", currentY);
    currentY = addParagraph(doc, outputs.a3.curriculum_alignment || "", currentY);

    currentY = addH3(doc, "Accommodation pathway", currentY);
    currentY = addParagraph(doc, outputs.a3.accommodation_pathway || "", currentY);

    if (outputs.a3.instructional_gaps && outputs.a3.instructional_gaps.length > 0) {
      currentY = addH3(doc, "Instructional gaps", currentY);
      for (let gi = 0; gi < outputs.a3.instructional_gaps.length; gi++) {
        const g = outputs.a3.instructional_gaps[gi];
        currentY = addItem(doc, currentY, {
          body: g.gap || "",
          chipLabel: (g.confidence || "?") + " confidence",
          chip: confidenceChip(g.confidence),
          cite: g.authority_cited ? "Cites " + g.authority_cited : undefined,
        });
      }
    }
  }

  if (outputs.a1) {
    currentY = addH2(doc, "Pacing plan", currentY);

    if (outputs.a1.instructor_tier) {
      drawChip(doc, MARGIN_LEFT, currentY - 2, "Tier: " + outputs.a1.instructor_tier, GRAY_CHIP, false);
      currentY += 22;
    }

    if (outputs.a1.strength_framing) {
      currentY = addCallout(doc, currentY, {
        body: outputs.a1.strength_framing,
        bg: [248, 250, 252],
        border: HAIR,
      });
    }

    if (outputs.a1.pacing_plan && outputs.a1.pacing_plan.length > 0) {
      for (let pj = 0; pj < outputs.a1.pacing_plan.length; pj++) {
        const phase = outputs.a1.pacing_plan[pj];
        const ptitle = String(phase.phase || "").replace(
          /^\s*phase\s*\d+\s*[:\-–—]\s*/i,
          ""
        );
        currentY = addPhase(
          doc,
          currentY,
          pj + 1,
          ptitle,
          phase.weeks,
          phase.focus || "",
          pj === outputs.a1.pacing_plan.length - 1
        );
      }
    }

    if (outputs.a1.instructor_guidance_band) {
      currentY = addH3(doc, "Instructor guidance", currentY);
      currentY = addParagraph(doc, outputs.a1.instructor_guidance_band, currentY);
    }
  }

  if (outputs.a4) {
    currentY = addH2(doc, "Classroom operations", currentY);

    if (outputs.a4.booth_rotation && outputs.a4.booth_rotation.pattern) {
      currentY = addParagraph(doc, "Booth rotation: " + outputs.a4.booth_rotation.pattern, currentY);
    }
    if (outputs.a4.equipment_fit_analysis) {
      currentY = addParagraph(doc, outputs.a4.equipment_fit_analysis, currentY);
    }

    if (outputs.a4.operational_bottlenecks && outputs.a4.operational_bottlenecks.length > 0) {
      currentY = addH3(doc, "Operational bottlenecks", currentY);
      for (let bk = 0; bk < outputs.a4.operational_bottlenecks.length; bk++) {
        const b = outputs.a4.operational_bottlenecks[bk];
        currentY = addItem(doc, currentY, {
          lead: b.bottleneck || "",
          chipLabel: b.severity || "?",
          chip: severityChip(b.severity),
          sub: b.mitigation ? "Mitigation: " + b.mitigation : undefined,
        });
      }
    }
  }

  if (outputs.a2 && outputs.a2.challenges && outputs.a2.challenges.length > 0) {
    currentY = addH2(doc, "What we flagged", currentY);
    currentY = addParagraph(
      doc,
      "A-2 Challenger reviewed the plan and flagged the following — verdict: " +
        (outputs.a2.verdict || "n/a") +
        ".",
      currentY,
      { muted: true }
    );
    for (let qi = 0; qi < outputs.a2.challenges.length; qi++) {
      const c = outputs.a2.challenges[qi];
      currentY = addItem(doc, currentY, {
        lead: c.flag || "",
        body: c.description || "",
        chipLabel: c.severity || "?",
        chip: severityChip(c.severity),
        cite: c.numeric_citation || undefined,
      });
    }
  }

  currentY = addCallout(doc, currentY, {
    title: "Want plate-level analysis?",
    body: "Whatever curriculum your school uses, forgedweldscan tells you whether your students are on track to pass their certification test — before test day. It evaluates every weld against the same acceptance logic industrial inspectors use, and ranks failure modes by clause-level dominance.",
    emphasis: "$1,500/year/school, up to 100 students.",
    note: "Pairs with any curriculum — AWS SENSE, NCCER, AWP, TX TEKS, or your own.",
    bg: ACCENT_TINT,
    border: ACCENT_BORDER,
    titleColor: INK,
  });

  if (report.calibrated_abstention_notes) {
    currentY = addCallout(doc, currentY, {
      title: "Where we abstained",
      body: report.calibrated_abstention_notes,
      bg: [250, 250, 250],
      border: HAIR,
      titleColor: INK,
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let pi = 1; pi <= pageCount; pi++) {
    doc.setPage(pi);
    drawHeader(doc);
    drawFooter(doc, pi, pageCount);
  }

  doc.save("forgedsight-plan-" + report.report_id + ".pdf");
}

function drawHeader(doc: jsPDF) {
  doc.setFillColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.rect(0, 0, PAGE_WIDTH, 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.text("forged", MARGIN_LEFT, 42);
  const fwWidth = doc.getTextWidth("forged");
  doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.text("sight", MARGIN_LEFT + fwWidth, 42);
  const swWidth = doc.getTextWidth("sight");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text("Strategic plan", MARGIN_LEFT + fwWidth + swWidth + 10, 42);
  doc.text("forgedsight.com", PAGE_WIDTH - MARGIN_RIGHT, 42, { align: "right" });

  doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_LEFT, 52, PAGE_WIDTH - MARGIN_RIGHT, 52);
  doc.setTextColor(0, 0, 0);
}

function drawFooter(doc: jsPDF, pageNum: number, pageCount?: number) {
  doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_LEFT, PAGE_HEIGHT - 44, PAGE_WIDTH - MARGIN_RIGHT, PAGE_HEIGHT - 44);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text("Confidential — FORGED Educational Systems", MARGIN_LEFT, PAGE_HEIGHT - 28);

  const pageLabel = pageCount ? "Page " + pageNum + " of " + pageCount : "Page " + pageNum;
  doc.text(pageLabel, PAGE_WIDTH - MARGIN_RIGHT, PAGE_HEIGHT - 28, { align: "right" });
  doc.setTextColor(0, 0, 0);
}

function drawChip(
  doc: jsPDF,
  x: number,
  y: number,
  label: string,
  style: ChipStyle,
  rightAlign?: boolean
): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  const txt = sanitizePdfText(String(label).toUpperCase());
  const w = doc.getTextWidth(txt) + 12;
  const h = 13;
  const bx = rightAlign ? x - w : x;
  doc.setFillColor(style.bg[0], style.bg[1], style.bg[2]);
  doc.setDrawColor(style.border[0], style.border[1], style.border[2]);
  doc.setLineWidth(0.5);
  doc.roundedRect(bx, y, w, h, 6, 6, "FD");
  doc.setTextColor(style.fg[0], style.fg[1], style.fg[2]);
  doc.text(txt, bx + 6, y + 9);
  doc.setTextColor(0, 0, 0);
  return w;
}

function drawOutcomeHero(doc: jsPDF, y: number, pct: number, ci: number[] | null): number {
  const hasCi = ci != null && ci.length === 2;
  const boxH = hasCi ? 124 : 80;
  const pad = 16;
  const top = pageBreakIfNeeded(doc, y, boxH + 10);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
  doc.setLineWidth(0.75);
  doc.roundedRect(MARGIN_LEFT, top, CONTENT_WIDTH, boxH, 8, 8, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text("PROJECTED COHORT OUTCOME", MARGIN_LEFT + pad, top + 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  const numText = pct + "%";
  doc.text(numText, MARGIN_LEFT + pad, top + 56);
  const numW = doc.getTextWidth(numText);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text("projected pass rate", MARGIN_LEFT + pad + numW + 8, top + 56);

  if (hasCi && ci) {
    const lo = Math.max(0, Math.min(100, Math.round(ci[0])));
    const hi = Math.max(0, Math.min(100, Math.round(ci[1])));
    const p = Math.max(0, Math.min(100, pct));

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text("95% confidence interval: " + lo + "% to " + hi + "%", MARGIN_LEFT + pad, top + 78);

    const trackX = MARGIN_LEFT + pad;
    const trackW = CONTENT_WIDTH - pad * 2;
    const trackY = top + 88;
    const trackH = 8;

    doc.setFillColor(238, 240, 243);
    doc.roundedRect(trackX, trackY, trackW, trackH, 4, 4, "F");

    const bandX = trackX + (lo / 100) * trackW;
    const bandW = ((hi - lo) / 100) * trackW;
    doc.setFillColor(ACCENT_BORDER[0], ACCENT_BORDER[1], ACCENT_BORDER[2]);
    doc.roundedRect(bandX, trackY, bandW > 1 ? bandW : 1, trackH, 4, 4, "F");

    const mx = trackX + (p / 100) * trackW;
    doc.setFillColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.rect(mx - 1.5, trackY - 4, 3, trackH + 8, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    doc.text("0%", trackX, trackY + 20);
    doc.text("50%", trackX + trackW / 2, trackY + 20, { align: "center" });
    doc.text("100%", trackX + trackW, trackY + 20, { align: "right" });
  }

  doc.setTextColor(0, 0, 0);
  return top + boxH + 16;
}

function addH2(doc: jsPDF, text: string, y: number): number {
  const nextY = pageBreakIfNeeded(doc, y, 44);
  doc.setFillColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.rect(MARGIN_LEFT, nextY - 11, 4, 15, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.text(sanitizePdfText(text), MARGIN_LEFT + 12, nextY);
  doc.setTextColor(0, 0, 0);
  return nextY + 24;
}

function addH3(doc: jsPDF, text: string, y: number): number {
  const nextY = pageBreakIfNeeded(doc, y, 30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.text(sanitizePdfText(text), MARGIN_LEFT, nextY);
  doc.setTextColor(0, 0, 0);
  return nextY + 16;
}

function addParagraph(
  doc: jsPDF,
  text: string,
  y: number,
  opts?: { bold?: boolean; muted?: boolean }
): number {
  if (!text) return y;
  doc.setFont("helvetica", opts && opts.bold ? "bold" : "normal");
  doc.setFontSize(10);
  if (opts && opts.muted) doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  else doc.setTextColor(BODY[0], BODY[1], BODY[2]);

  const clean = sanitizePdfText(text);
  const lines = doc.splitTextToSize(clean, CONTENT_WIDTH) as string[];
  const lineHeight = 14;
  let nextY = y;
  for (let i = 0; i < lines.length; i++) {
    nextY = pageBreakIfNeeded(doc, nextY, lineHeight + 4);
    doc.text(lines[i], MARGIN_LEFT, nextY);
    nextY += lineHeight;
  }
  doc.setTextColor(0, 0, 0);
  return nextY + 8;
}

function addItem(doc: jsPDF, y: number, opts: ItemOpts): number {
  const leftPad = 16;
  const innerX = MARGIN_LEFT + leftPad;
  const innerW = CONTENT_WIDTH - leftPad;

  const leadLines = opts.lead
    ? (doc.splitTextToSize(sanitizePdfText(opts.lead), innerW) as string[])
    : [];
  const bodyLines = opts.body
    ? (doc.splitTextToSize(sanitizePdfText(opts.body), innerW) as string[])
    : [];
  const citeLines = opts.cite
    ? (doc.splitTextToSize(sanitizePdfText(opts.cite), innerW) as string[])
    : [];
  const subLines = opts.sub
    ? (doc.splitTextToSize(sanitizePdfText(opts.sub), innerW - 16) as string[])
    : [];

  let est = 0;
  if (opts.chipLabel) est += 16;
  est += leadLines.length * 13;
  est += bodyLines.length * 13;
  if (citeLines.length) est += 4 + citeLines.length * 11;
  if (subLines.length) est += 6 + (subLines.length * 12 + 12);
  est += 18;

  const top = pageBreakIfNeeded(doc, y, est);
  let yy: number;

  if (opts.chipLabel && opts.chip) {
    drawChip(doc, MARGIN_LEFT + CONTENT_WIDTH, top, opts.chipLabel, opts.chip, true);
    doc.setFillColor(opts.chip.fg[0], opts.chip.fg[1], opts.chip.fg[2]);
    doc.roundedRect(MARGIN_LEFT, top + 1, 8, 8, 2, 2, "F");
    yy = top + 22;
  } else {
    doc.setFillColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.roundedRect(MARGIN_LEFT, top + 3, 8, 8, 2, 2, "F");
    yy = top + 11;
  }

  if (leadLines.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(INK[0], INK[1], INK[2]);
    for (let i = 0; i < leadLines.length; i++) {
      doc.text(leadLines[i], innerX, yy);
      yy += 13;
    }
  }

  if (bodyLines.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(BODY[0], BODY[1], BODY[2]);
    for (let i = 0; i < bodyLines.length; i++) {
      doc.text(bodyLines[i], innerX, yy);
      yy += 13;
    }
  }

  if (citeLines.length) {
    yy += 2;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    for (let i = 0; i < citeLines.length; i++) {
      doc.text(citeLines[i], innerX, yy);
      yy += 11;
    }
  }

  if (subLines.length) {
    yy += 4;
    const boxTop = yy - 9;
    const boxH = subLines.length * 12 + 12;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
    doc.setLineWidth(0.5);
    doc.roundedRect(innerX, boxTop, innerW, boxH, 3, 3, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(BODY[0], BODY[1], BODY[2]);
    let sy = boxTop + 13;
    for (let i = 0; i < subLines.length; i++) {
      doc.text(subLines[i], innerX + 8, sy);
      sy += 12;
    }
    yy = boxTop + boxH + 6;
  }

  yy += 8;
  doc.setDrawColor(HAIR[0], HAIR[1], HAIR[2]);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_LEFT, yy, MARGIN_LEFT + CONTENT_WIDTH, yy);
  doc.setTextColor(0, 0, 0);
  return yy + 12;
}

function addPhase(
  doc: jsPDF,
  y: number,
  num: number,
  title: string,
  weeks: any,
  focus: string,
  isLast: boolean
): number {
  const circleX = MARGIN_LEFT + 11;
  const textX = MARGIN_LEFT + 34;
  const textW = CONTENT_WIDTH - 34;

  const focusLines = doc.splitTextToSize(sanitizePdfText(focus || ""), textW) as string[];
  const est = Math.max(26, 16 + focusLines.length * 12) + 16;
  const top = pageBreakIfNeeded(doc, y, est);

  const cy = top + 9;
  const fill = isLast ? GREEN_CHIP.fg : ACCENT;
  doc.setFillColor(fill[0], fill[1], fill[2]);
  doc.circle(circleX, cy, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(String(num), circleX, cy + 3.5, { align: "center" });

  let titleMax = textW;
  const weeksLabel =
    weeks != null ? weeks + (Number(weeks) === 1 ? " week" : " weeks") : "";
  if (weeksLabel) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    const pillW = doc.getTextWidth(weeksLabel.toUpperCase()) + 12;
    titleMax = textW - pillW - 8;
    drawChip(doc, MARGIN_LEFT + CONTENT_WIDTH, top + 1, weeksLabel, {
      fg: ACCENT,
      bg: ACCENT_TINT,
      border: ACCENT_BORDER,
    }, true);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  const titleLines = doc.splitTextToSize(sanitizePdfText(title || ""), titleMax > 60 ? titleMax : 60) as string[];
  let yy = top + 9;
  for (let i = 0; i < titleLines.length; i++) {
    doc.text(titleLines[i], textX, yy);
    yy += 13;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(BODY[0], BODY[1], BODY[2]);
  for (let i = 0; i < focusLines.length; i++) {
    doc.text(focusLines[i], textX, yy);
    yy += 12;
  }

  if (!isLast) {
    doc.setDrawColor(ACCENT_BORDER[0], ACCENT_BORDER[1], ACCENT_BORDER[2]);
    doc.setLineWidth(1.5);
    doc.line(circleX, cy + 12, circleX, yy + 4);
  }

  doc.setTextColor(0, 0, 0);
  return yy + 10;
}

function addCallout(doc: jsPDF, y: number, opts: CalloutOpts): number {
  const pad = 14;
  const innerW = CONTENT_WIDTH - pad * 2;

  const bodyLines = doc.splitTextToSize(sanitizePdfText(opts.body), innerW) as string[];
  const emphLines = opts.emphasis
    ? (doc.splitTextToSize(sanitizePdfText(opts.emphasis), innerW) as string[])
    : [];
  const noteLines = opts.note
    ? (doc.splitTextToSize(sanitizePdfText(opts.note), innerW) as string[])
    : [];

  let h = pad;
  if (opts.title) h += 20;
  h += bodyLines.length * 14;
  if (emphLines.length) h += 4 + emphLines.length * 14;
  if (noteLines.length) h += 4 + noteLines.length * 12;
  h += pad;

  const top = pageBreakIfNeeded(doc, y, h + 8);

  doc.setFillColor(opts.bg[0], opts.bg[1], opts.bg[2]);
  doc.setDrawColor(opts.border[0], opts.border[1], opts.border[2]);
  doc.setLineWidth(0.75);
  doc.roundedRect(MARGIN_LEFT, top, CONTENT_WIDTH, h, 7, 7, "FD");

  let yy = top + pad + 6;
  if (opts.title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    const tc = opts.titleColor || INK;
    doc.setTextColor(tc[0], tc[1], tc[2]);
    doc.text(sanitizePdfText(opts.title), MARGIN_LEFT + pad, yy);
    yy += 20;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(BODY[0], BODY[1], BODY[2]);
  for (let i = 0; i < bodyLines.length; i++) {
    doc.text(bodyLines[i], MARGIN_LEFT + pad, yy);
    yy += 14;
  }

  if (emphLines.length) {
    yy += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    for (let i = 0; i < emphLines.length; i++) {
      doc.text(emphLines[i], MARGIN_LEFT + pad, yy);
      yy += 14;
    }
  }

  if (noteLines.length) {
    yy += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    for (let i = 0; i < noteLines.length; i++) {
      doc.text(noteLines[i], MARGIN_LEFT + pad, yy);
      yy += 12;
    }
  }

  doc.setTextColor(0, 0, 0);
  return top + h + 14;
}

function pageBreakIfNeeded(doc: jsPDF, y: number, neededHeight: number): number {
  if (y + neededHeight > CONTENT_BOTTOM) {
    doc.addPage();
    return MARGIN_TOP;
  }
  return y;
}

function sanitizePdfText(text: any): string {
  if (text == null) return "";
  var s = String(text);
  s = s.replace(/[­​‌‍﻿]/g, "");
  s = s.replace(/ /g, " ");
  s = s
    .replace(/≈/g, "~")
    .replace(/−/g, "-")
    .replace(/≤/g, "<=")
    .replace(/≥/g, ">=")
    .replace(/≠/g, "!=")
    .replace(/→/g, "->")
    .replace(/←/g, "<-")
    .replace(/↑/g, "^")
    .replace(/↓/g, "v")
    .replace(/…/g, "...")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"');
  return s;
}
